import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import {DatePicker, DateTimePicker, TimePicker} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {useField} from 'formik';
import PropTypes from 'prop-types';

// This implementation is specific to AdapterDateFns accepted normalization.
const normalizePattern = (pattern = '') => {
  return pattern
    .replace(/dddd/g, 'EEEE') // Day of week (long)
    .replace(/ddd/g, 'EEE') // Day of week (short)
    .replace(/YYYY/g, 'yyyy') // Year
    .replace(/YY/g, 'yy') // Year (short)
    .replace(/DD/g, 'dd') // Day of month (leading zero)
    .replace(/D/g, 'd') // Day of month (no leading zero)
    .replace(/[Aa]/g, 'a'); // AM/PM (date-fns uses 'a' for both)
};

const AppDateTimePicker = ({readonly, ...props}) => {
  const [field, meta, helpers] = useField(props.name);
  const {setValue} = helpers;

  const errorText = meta.touched && meta.error ? meta.error : '';

  const pattern = normalizePattern(props.pattern ?? 'MMM dd, yyyy');
  const hasTime = /(H|h|m|s|a)/.test(pattern);
  const hasDate = /(y|M|d)/.test(pattern);

  const minDate = props.mindatetime ? new Date(props.mindatetime) : undefined;
  const maxDate = props.maxdatetime ? new Date(props.maxdatetime) : undefined;

  const Component =
    hasDate && hasTime ? DateTimePicker : hasDate ? DatePicker : TimePicker;

  return (
    <FormControl fullWidth error={!!errorText}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Component
          {...props}
          name={field.name}
          value={field.value ?? null}
          inputFormat={pattern}
          disableMaskedInput
          minDateTime={minDate}
          maxDateTime={maxDate}
          readOnly={readonly}
          onChange={(val) => {
            if (readonly) return;
            setValue(val && !isNaN(val) ? val : null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errorText}
              helperText={errorText}
              required={props.required}
              inputRef={params.inputRef}
            />
          )}
        />
      </LocalizationProvider>
    </FormControl>
  );
};

AppDateTimePicker.propTypes = {
  name: PropTypes.string.isRequired,

  /** Labeling */
  label: PropTypes.string,
  required: PropTypes.bool,
  readonly: PropTypes.bool,

  /** Date constraints */
  mindatetime: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  maxdatetime: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
  /** Display / parsing */
  pattern: PropTypes.string,

  /** MUI DatePicker flags */
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
};

AppDateTimePicker.defaultProps = {
  disablePast: false,
  disableFuture: false,
  pattern: 'MMM dd, yyyy',
};

export default AppDateTimePicker;
